// src/components/resources/tools/generators/FakeChat/IMessagePreview.tsx

'use client'

import { cn } from '~/lib/utils'
import { useFakeChatState } from './FakeChatContext'
import type { ChatMessage, ChatParticipant, MessageStatus } from './FakeChat.types'
import { useResolvedMessages } from './useResolvedMessages'
import { statusBarTimeFrom } from './IosStatusBar'
import { getTheme, type AccentTheme } from './accentThemes'

/**
 * Props for IMessageBubble component
 * @interface IMessageBubbleProps
 * @property {ChatMessage} message - The message to display.
 * @property {ChatParticipant} [participant] - The participant who sent the message.
 * @property {AccentTheme} theme - The theme to use for the message bubble.
 * @property {boolean} showTimestamps - Whether to show the timestamp.
 * @property {boolean} showStatus - Whether to show the delivery status.
 */
interface IMessageBubbleProps {
  message: ChatMessage
  participant?: ChatParticipant
  isSelf: boolean
  theme: AccentTheme
  showTimestamps: boolean
  showStatus: boolean
  deliveryStatus: MessageStatus
}

/**
 * Individual message bubble component for iMessage style
 * Uses fixed brand colors: #007AFF for outgoing, #E9E9EB for incoming
 */
function IMessageBubble({
  message,
  participant,
  isSelf,
  theme: _theme,
  showTimestamps: _showTimestamps,
  showStatus,
  deliveryStatus
}: IMessageBubbleProps): JSX.Element {
  // iMessage blue: #007AFF, gray: #E9E9EB, text: #fff/#000, bubble radius: 14px, px: 10, py: 6
  const bubbleBg = isSelf ? '#007AFF' : '#E9E9EB'
  const textColor = isSelf ? 'text-white' : 'text-black'
  const bubbleTailClass = isSelf
    ? 'right-[-4.3px] scale-x-100'
    : 'left-[-4.3px] scale-x-[-1]'
  const bubbleTailColor = isSelf ? '#007AFF' : '#E9E9EB'
  const maxBubbleWidth = 'max-w-[222px]'
  const fontSize = 'text-[13px]'
  const bubbleRadius = 'rounded-[14px]'
  const px = 'px-[10px]'
  const py = 'py-[6px]'
  const bubbleShadow = ''
  const bubbleTextClass =
    'whitespace-pre-wrap break-words [&_[data-type=emoji]]:inline-block [&_[data-type=emoji]]:size-[14px] [&_[data-type=emoji]]:align-text-bottom'
  // For status (e.g. Not Delivered) - only red if not delivered
  const statusColor = deliveryStatus === 'not-delivered' ? 'text-[#FD3B31]' : 'text-[#8A898E]'

  // For group: show avatar and name for non-self
  const showAvatar = !isSelf && participant?.avatarInitials
  const showName = !isSelf && participant?.displayName

  return (
    <div className={cn('flex flex-col gap-[1px] pb-[6px]')}>
      <div className={cn('flex w-full items-end gap-[4px]')}>
        {/* Avatar for incoming */}
        {showAvatar ? (
          <div className="size-[24px]">
            <div className="overflow-hidden rounded-full size-[24px] text-[10px]">
              <div className="flex size-full items-center justify-center bg-gradient-to-b from-[#A5ABB9] to-[#858994] text-center">
                <span className="font-semibold text-white">
                  {participant.displayName?.charAt(0).toUpperCase() ?? 'A'}
                </span>
              </div>
            </div>
          </div>
        ) : null}
        <div className={cn('flex flex-1 flex-col', isSelf ? 'items-end' : 'items-start')}>
          {/* Name for incoming */}
          {showName ? (
            <span className="ml-[10px] text-[9px] text-[#8A898E]">{participant.displayName}</span>
          ) : null}
          <div className="flex flex-col gap-[2px]">
            <div className={cn('flex items-center gap-[6px]')}>
              <div
                className={cn(
                  'relative',
                  maxBubbleWidth,
                  bubbleRadius,
                  px,
                  py,
                  bubbleShadow
                )}
                style={{ background: bubbleBg }}
              >
                <div className={cn(fontSize, bubbleTextClass, textColor, 'relative z-10')}>
                  {message.text}
                </div>
                {/* Bubble tail */}
                <svg
                  viewBox="0 0 17 21"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className={cn(
                    'absolute bottom-[-1px] h-[18px] z-0',
                    bubbleTailClass,
                    isSelf ? 'text-[#007AFF]' : 'text-[#E9E9EB]'
                  )}
                  style={{
                    [isSelf ? 'right' : 'left']: 0,
                    color: bubbleTailColor
                  }}
                  aria-hidden
                >
                  <path
                    d="M16.8869 20.1846C11.6869 20.9846 6.55352 18.1212 4.88685 16.2879C6.60472 12.1914 -4.00107 2.24186 2.99893 2.24148C4.61754 2.24148 6 -1.9986 11.8869 1.1846C11.9081 2.47144 11.8869 6.92582 11.8869 7.6842C11.8869 18.1842 17.8869 19.5813 16.8869 20.1846Z"
                    fill="currentColor"
                  />
                </svg>
              </div>
              {/* Attachments (rare in iMessage, but keep for completeness) */}
              {message.attachments?.map(attachment => (
                <div
                  key={attachment.id}
                  className={cn(
                    'max-w-[200px] rounded-2xl border border-[#d1d1d6] bg-white px-3 py-2 text-xs text-[#1d1c1d] shadow-sm',
                    isSelf ? 'self-end' : 'self-start'
                  )}
                >
                  <p className="font-medium text-[11px] leading-tight">{attachment.title}</p>
                  {attachment.description ? (
                    <p className="mt-1 text-[10px] text-[#6e6e73] leading-tight">{attachment.description}</p>
                  ) : null}
                </div>
              ))}
            </div>
          </div>
          {/* Status for last self message */}
          {message.status && isSelf && showStatus ? (
            <div className={cn('flex justify-end', 'text-[9px] font-semibold', statusColor)}>
              {message.status.charAt(0).toUpperCase() + message.status.slice(1)}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  )
}

/**
 * iMessage platform preview component
 * Provides an accurate iMessage interface with proper status bar, header, and group chat support
 */
export function IMessagePreview(): JSX.Element {
  const { conversation } = useFakeChatState()
  const theme = getTheme(conversation.metadata.accentColor)
  const resolvedMessages = useResolvedMessages(conversation.messages)
  const conversationStartTime = resolvedMessages[0]?.message.timestamp ?? '9:41 AM'
  const statusBarTime = statusBarTimeFrom(conversationStartTime)
  const dayLabel = conversation.metadata.showTimestamps
    ? `Today ${conversationStartTime}`
    : 'Today'
  const contactInitial = conversation.metadata.channelName.charAt(0)?.toUpperCase() ?? 'A'
  const lastSelfMessageId = [...resolvedMessages].reverse().find(m => m.isSelf)?.message.id

  // For group: show avatars in header
  const groupParticipants = conversation.participants?.filter(p => !p.isSelf) ?? []
  const participantCount = groupParticipants.length + 1

  return (
    <div className="relative flex w-full justify-center p-6">
      <div className="relative h-[760px] w-[360px] rounded-[54px] border border-black/40 bg-black/95 p-4 shadow-[0_30px_70px_rgba(15,23,42,0.35)]">
        <div
          className="relative flex h-full flex-col overflow-hidden rounded-[44px]"
          style={{
            background: '#f9f9f9',
            fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', system-ui, sans-serif"
          }}
        >
          {/* Notch */}
          <div className="absolute left-1/2 top-3 h-7 w-32 -translate-x-1/2 rounded-full bg-black" aria-hidden />
          {/* Status bar */}
          <div className="flex items-center justify-between px-6 pt-3 text-[14px] font-semibold text-black select-none">
            <span>{statusBarTime}</span>
            <div className="flex items-center gap-1.5 text-black">
              <span className="flex items-end gap-0.5" aria-hidden>
                <span className="h-1.5 w-0.5 rounded-full bg-black" />
                <span className="h-2 w-0.5 rounded-full bg-black" />
                <span className="h-2.5 w-0.5 rounded-full bg-black" />
                <span className="h-3 w-0.5 rounded-full bg-black" />
              </span>
              <svg width="16" height="12" viewBox="0 0 16 12" fill="none" aria-hidden>
                <path d="M8 0C4.13 0 0.74 1.86 0 4.15l1.04 1.04C1.55 3.34 4.46 1.5 8 1.5c3.54 0 6.45 1.84 6.96 3.69L16 4.15C15.26 1.86 11.87 0 8 0Z" fill="black" />
                <path d="M8 3C5.5 3 3.34 4.35 2.75 5.75l1.01 1.01C4.21 5.6 5.94 4.5 8 4.5s3.79 1.1 4.24 2.26l1.01-1.01C12.66 4.35 10.5 3 8 3Z" fill="black" />
                <path d="M8 6C6.87 6 5.91 6.54 5.5 7.25l1.05 1.05C6.77 7.96 7.33 7.5 8 7.5s1.23 0.46 1.45 0.8l1.05-1.05C10.09 6.54 9.13 6 8 6Z" fill="black" />
                <circle cx="8" cy="9.5" r="1.2" fill="black" />
              </svg>
              <svg width="24" height="12" viewBox="0 0 25 12" fill="none" aria-hidden>
                <rect x="1" y="1" width="19" height="8" rx="2" stroke="black" strokeWidth="1.5" />
                <rect x="21" y="3" width="1.5" height="4" rx="0.75" fill="black" />
                <rect x="2" y="2" width="17" height="6" rx="1" fill="#34C759" />
              </svg>
            </div>
          </div>
        {/* Chat area */}
        <div className="flex h-full flex-col">
          {/* Header */}
          <div className="relative flex flex-col pt-[35px] pb-[6px] bg-[#F6F5F6]">
            <div className="relative flex items-center justify-center">
              {/* Back button and unread badge */}
              <div className="absolute left-[12px] flex items-center">
                <span className="flex items-center">
                  <span className="inline-flex items-center">
                    <svg viewBox="0 0 18 23" fill="none" className="size-[17px] text-[#007AFF]" aria-hidden>
                      <path d="M0 11.3086C0 11.8506 0.195312 12.2998 0.644531 12.7393L10.2881 22.1191C10.625 22.4561 11.0352 22.6221 11.5137 22.6221C12.5 22.6221 13.2959 21.8311 13.2959 20.8545C13.2959 20.3613 13.0908 19.9219 12.7344 19.5605L4.17969 11.2988L12.7344 3.04688C13.1006 2.69043 13.2959 2.23633 13.2959 1.75781C13.2959 0.78125 12.5 0 11.5137 0C11.0303 0 10.625 0.161133 10.2881 0.498047L0.644531 9.87793C0.200195 10.3076 0.00488281 10.752 0 11.3086Z" fill="currentColor" />
                    </svg>
                  </span>
                  <div className="mt-px -ml-[3px] flex h-[14px] min-w-[14px] items-center justify-center rounded-full bg-[#007AFF] px-[4px] text-[8px] font-medium text-white">
                    92
                  </div>
                </span>
              </div>
              {/* Group avatars */}
              <div className="relative size-[40px] text-[18px]">
                {/* Single participant - full size centered */}
                {groupParticipants.length === 1 && (
                  <div className="overflow-hidden rounded-full size-[40px] text-[18px]">
                    <div className="flex size-full items-center justify-center bg-gradient-to-b from-[#A5ABB9] to-[#858994] text-center">
                      <span className="font-semibold text-white">{contactInitial}</span>
                    </div>
                  </div>
                )}
                {/* Two participants - stacked with side offset */}
                {groupParticipants.length === 2 && (
                  <>
                    <div className="overflow-hidden rounded-full absolute left-[-5px] size-[28px] text-[13px] bottom-[7px]">
                      <div className="flex size-full items-center justify-center bg-gradient-to-b from-[#A5ABB9] to-[#858994] text-center">
                        <span className="font-semibold text-white">
                          {groupParticipants[0]?.displayName?.charAt(0).toUpperCase() ?? 'T'}
                        </span>
                      </div>
                    </div>
                    <div className="overflow-hidden rounded-full absolute left-[23px] size-[24px] text-[11px] bottom-[0px]">
                      <div className="flex size-full items-center justify-center bg-gradient-to-b from-[#A5ABB9] to-[#858994] text-center">
                        <span className="font-semibold text-white">{contactInitial}</span>
                      </div>
                    </div>
                  </>
                )}
                {/* Three or more participants - stacked with side offset */}
                {groupParticipants.length >= 3 && (
                  <>
                    <div className="overflow-hidden rounded-full absolute left-[-5px] size-[28px] text-[13px] bottom-[16px]">
                      <div className="flex size-full items-center justify-center bg-gradient-to-b from-[#A5ABB9] to-[#858994] text-center">
                        <span className="font-semibold text-white">
                          {groupParticipants[0]?.displayName?.charAt(0).toUpperCase() ?? 'T'}
                        </span>
                      </div>
                    </div>
                    <div className="overflow-hidden rounded-full absolute left-[23px] size-[24px] text-[11px] bottom-[9px]">
                      <div className="flex size-full items-center justify-center bg-gradient-to-b from-[#A5ABB9] to-[#858994] text-center">
                        <span className="font-semibold text-white">
                          {groupParticipants[1]?.displayName?.charAt(0).toUpperCase() ?? 'T'}
                        </span>
                      </div>
                    </div>
                    <div className="overflow-hidden rounded-full absolute bottom-[0px] left-[9px] size-[16px] text-[7px]">
                      <div className="flex size-full items-center justify-center bg-gradient-to-b from-[#A5ABB9] to-[#858994] text-center">
                        <span className="font-semibold text-white">
                          {groupParticipants[2]?.displayName?.charAt(0).toUpperCase() ?? contactInitial}
                        </span>
                      </div>
                    </div>
                  </>
                )}
              </div>
              {/* Camera/Info button */}
              <span className="absolute right-[17px]">
                <svg viewBox="0 0 24 24" fill="none" className="size-[25px] text-[#007AFF]" aria-hidden>
                  <rect x="3" y="5" width="18" height="14" rx="3" stroke="currentColor" strokeWidth="1.8" />
                  <path d="M7 5l2-2h6l2 2" stroke="currentColor" strokeWidth="1.8" />
                </svg>
              </span>
            </div>
            {/* Group info: member count */}
            <div className="mx-auto mt-[2.5px] flex max-w-[65%] items-center justify-center overflow-hidden">
              <span className="truncate text-[9px] text-black">
                {/* Show participantCount - 1 for group size, since one is the originator */}
                {(participantCount > 1 ? participantCount - 1 : 1)} {(participantCount - 1 === 1 ? 'Person' : 'People')}
              </span>
              <svg viewBox="0 0 24 24" fill="none" className="size-[9px] shrink-0 text-[#C1C1C3]" aria-hidden>
                <path d="M16.535 11.7486C16.5295 11.3958 16.402 11.1014 16.1174 10.8254L9.48535 4.32854C9.25873 4.10782 8.99214 4 8.66634 4C8.02338 4 7.5 4.51201 7.5 5.15812C7.5 5.47528 7.63016 5.76971 7.8642 6.00414L13.7659 11.7459L7.8642 17.4931C7.6329 17.7248 7.5 18.016 7.5 18.3419C7.5 18.9852 8.02338 19.5 8.66634 19.5C8.98665 19.5 9.25873 19.3921 9.48535 19.1656L16.1174 12.6718C16.4048 12.39 16.535 12.0987 16.535 11.7486Z" fill="currentColor" />
              </svg>
            </div>
            {/* Divider */}
            <svg className="absolute inset-x-0 bottom-0" height="1" width="100%">
              <line className="stroke-[#B2B2B2]" x1="0" y1="0" x2="100%" y2="0" strokeWidth="1" />
            </svg>
          </div>
          {/* Chat messages area */}
          <div className="no-scrollbar relative flex flex-1 flex-col overflow-auto px-[11px] bg-white">
            {/* Day label */}
            <div className="flex justify-center pt-[9px] pb-[6px] text-center">
              <span className="text-[9px] font-medium text-[#8A898E]">{dayLabel}</span>
            </div>
            {/* Messages */}
            <div className="flex flex-col gap-[1px] pb-[6px]">
              {resolvedMessages.map(({ message, participant, isSelf }) => (
                <IMessageBubble
                  key={message.id}
                  message={message}
                  participant={participant}
                  isSelf={isSelf}
                  theme={theme}
                  showTimestamps={false}
                  showStatus={message.id === lastSelfMessageId}
                  deliveryStatus={message.status ?? 'delivered'}
                />
              ))}
              {/* Typing indicator */}
              {conversation.metadata.typingIndicator ? (
                <div className="flex justify-start pl-1 pb-2">
                  <div className="flex items-center gap-[3px] rounded-[18px] bg-[#e5e5ea] px-[11px] py-[9px]">
                    <span className={cn('h-[7px] w-[7px] animate-pulse rounded-full bg-[#8e8e93]')} />
                    <span className={cn('h-[7px] w-[7px] animate-pulse rounded-full bg-[#8e8e93]')} style={{ animationDelay: '0.15s' }} />
                    <span className={cn('h-[7px] w-[7px] animate-pulse rounded-full bg-[#8e8e93]')} style={{ animationDelay: '0.3s' }} />
                  </div>
                </div>
              ) : null}
            </div>
          </div>
          {/* Composer */}
          <div className="flex items-center gap-[12px] px-[12px] pt-[6px] pb-[12px] bg-[#FCFCFC]">
            <div className="flex size-[36px] items-center justify-center rounded-full bg-[#E8E7EC] text-[#7E7F84]">
              <svg viewBox="0 0 24 24" fill="none" className="size-[18px]" aria-hidden>
                <path d="M4 12.2616C4 12.9544 4.56808 13.5189 5.25732 13.5189H11.0074V19.269C11.0074 19.9551 11.5688 20.5232 12.2616 20.5232C12.9544 20.5232 13.5256 19.9551 13.5256 19.269V13.5189H19.269C19.9551 13.5189 20.5232 12.9544 20.5232 12.2616C20.5232 11.5688 19.9551 10.9976 19.269 10.9976H13.5256V5.25732C13.5256 4.56808 12.9544 4 12.2616 4C11.5688 4 11.0074 4.56808 11.0074 5.25732V10.9976H5.25732C4.56808 10.9976 4 11.5688 4 12.2616Z" fill="currentColor" />
              </svg>
            </div>
            <div className="flex flex-1 items-center overflow-hidden rounded-full border px-[12px] py-[8px] border-[#DEDEDE]">
              <span className="flex-1 truncate text-[15px] text-[#C1C2C4]">iMessage</span>
              <svg viewBox="0 0 24 24" fill="none" className="size-[18px] text-[#ACB4B7]" aria-hidden>
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                <path d="M8 12h8M12 8v8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </div>
          </div>
        </div>
        </div>
      </div>
    </div>
  )
}
